export class CustomAttributeOptionModel {
    constructor() { }
    Id: string;
    Label: string;
    IsDeleted: boolean;
}

export const mapToCustomAttributeOption = (x: any): CustomAttributeOptionModel => ({
    Id: x?.id ?? x?.Id ?? "",
    Label: x?.label ?? x?.Label ?? "",
    IsDeleted: !!(x?.isDeleted ?? x?.IsDeleted),
});

export type CustomAttributeFieldType = "TEXT" | "NUMBER" | "DATE" | "BOOLEAN" | "DROPDOWN" | "EMAIL" | "PHONE";

export const FIELD_TYPE_LABELS: Record<CustomAttributeFieldType, string> = {
    TEXT: "Text",
    NUMBER: "Number",
    DATE: "Date",
    BOOLEAN: "Boolean",
    DROPDOWN: "Drop Down",
    EMAIL: "Email",
    PHONE: "Phone"
};

export const FIELD_TYPE_ORDER: CustomAttributeFieldType[] = ["TEXT", "NUMBER", "DATE", "BOOLEAN", "DROPDOWN"];

export const fieldTypeToNumber = (code: CustomAttributeFieldType): number =>
    Math.max(0, FIELD_TYPE_ORDER.indexOf(code));

export const numberToFieldType = (n: number): CustomAttributeFieldType =>
    FIELD_TYPE_ORDER[n] ?? "TEXT";

export class CustomAttributePayloadModel {
    constructor() { }
    Id: string;
    Name: string;
    Reference: string;
    IsDeleted: boolean;
    Type: number;
    Options: CustomAttributeOptionModel[];
    Description: string;
    IsRequired: boolean;
    FieldType?: string;
    FieldTypeCode?: CustomAttributeFieldType;
    DropdownOptions?: string[];
    OptionsDetail?: { id: string; label: string }[];
}

export const mapToCustomAttributePayload = (x: any): CustomAttributePayloadModel => {
    const rawOptions = x.options ?? x.Options ?? [];
    const options = rawOptions.map(mapToCustomAttributeOption);
    const typeNum = x.type ?? x.Type ?? 0;
    const fieldTypeCode = numberToFieldType(typeNum);
    const activeOptions = options.filter((o) => !o.IsDeleted);

    return {
        Id: x.id ?? x.Id,
        Name: x.name ?? x.Name ?? "",
        Reference: x.reference ?? x.Reference ?? "",
        IsDeleted: !!(x.isDeleted ?? x.IsDeleted),
        Type: typeNum,
        Options: options,
        Description: x.description ?? x.Description ?? "",
        IsRequired: !!(x.isRequired ?? x.IsRequired),
        FieldType: FIELD_TYPE_LABELS[fieldTypeCode],
        FieldTypeCode: fieldTypeCode,
        DropdownOptions: activeOptions.map((o) => o.Label),
        OptionsDetail: activeOptions.map((o) => ({ id: o.Id, label: o.Label })),
    }
};

export const toApiPayload = (m: CustomAttributePayloadModel) => ({
    Id: m.Id,
    Name: m.Name,
    Reference: m.Reference,
    IsDeleted: m.IsDeleted,
    Type: m.Type,
    Options: m.Options.map((o) => ({ Id: o.Id, Label: o.Label, IsDeleted: o.IsDeleted })),
    Description: m.Description,
    IsRequired: m.IsRequired,
});

export class CustomAttributeDefinitionModel {
    constructor() {
    }
    Id?: string;
    Name: string;
    Reference: string;
    Type: number;
    IsRequired: boolean;
    IsDeleted?: boolean;
    Description?: string;
    DropdownOptions?: string[];
    OptionsDetail?: { id: string; label: string }[];
}

export const definitionFromPayloadModel = (
    m: Pick<
        CustomAttributePayloadModel,
        'Id' | 'Name' | 'Reference' | 'IsDeleted' | 'Type' | 'Options' | 'Description' | 'IsRequired'
    >
): CustomAttributeDefinitionModel => {
    const activeOptions = m.Options.filter((o) => !o.IsDeleted);
    return {
        Id: m.Id,
        Name: m.Name,
        Reference: m.Reference,
        Type: m.Type,
        IsRequired: m.IsRequired,
        IsDeleted: m.IsDeleted,
        Description: m.Description,
        DropdownOptions: activeOptions.map((o) => o.Label),
        OptionsDetail: activeOptions.map((o) => ({ id: o.Id, label: o.Label })),
    };
};

export const mapToCustomAttributeDefinition = (x: any): CustomAttributeDefinitionModel =>
    definitionFromPayloadModel(mapToCustomAttributePayload(x));

export enum CustomAttributeType {
    Text,
    Number,
    Date,
    Boolen,
    Dropdown,
    Email,
    Phone,
}